// Built-in action executors (plan 2026-07-28 platform assistant, step 0.4).
//
// Side-effect module: importing it registers every shipped action_type with the
// action registry. Import it once (side-effect only) in the module that renders
// ActionProposalCard so registration runs before the first card mounts.
import { createSegment, rebuildSegment } from "@/api/segments";
import {
  fileSupportAlert,
  triggerBackfillRun,
  triggerPipelineRun,
  isTransformScheduled,
  triggerTransformRun,
  updateManagedSource,
} from "@/api/managedSources";
import { fileSupportRequest } from "@/api/support";
import type { SupportTopic } from "@/api/support";
import { streamUntilInit } from "@/api/streamUntilInit";
import { createReport } from "@/api/reports";
import { buildEdition, editionBuildRefusal } from "@/api/editions";
import { refusalMessage } from "@/components/editions/editionCopy";
import { editionPath } from "@/components/editions/ReportEditionActions";
import { requestIterate } from "@/context/reportAssistantBridge";
import {
  createContextDocument,
  createContextEvent,
  deleteContextDocument,
  deleteContextEvent,
  getContextDocument,
  listContextEvents,
  updateBusinessProfile,
  updateContextDocument,
  updateContextEvent,
} from "@/api/context";
import type {
  BusinessProfilePatch,
  ContextEventCategory,
} from "@/api/context";
import { ApiError } from "@/api/client";
import { openSourceWizard } from "@/context/sourceWizardBridge";
import { openContinueInChat } from "@/context/continueInChatBridge";
import { openAuthorizationDrawer } from "@/context/authorizationDrawerBridge";
import type { CreateSegmentPayload } from "@/types/segments";
import { ACTION_CANCELLED, ActionError, registerAction } from "./registry";
// Custom metric cards (plan 2026-10-16 custom metrics through the MCP and the assistant).
import "./dashboardMetricActions";
import "./shareRequestActions";
import "./sourceRefreshActions";
import "./dataModelActions";
import { dashboardPath } from "@/utils/dashboardPath";

// create_segment — mirrors SegmentCreatedCard.handleCreate exactly: same REST
// call, same result payload, same "View Segment" navigation target.
registerAction("create_segment", async (params) => {
  const seg = await createSegment(params as unknown as CreateSegmentPayload);
  return {
    resultRef: { segment_id: seg.id },
    link: { label: "View Segment", to: `/activation/segments/${seg.id}` },
  };
});

// --- Managed-source run proposals (plan 2026-07-28, step 1.5) ---
// Each calls the same role-gated REST function the Flows UI uses, under the
// user's JWT. The Flows detail route is keyed by the managed source public id
// (`/data-management/flows/:id`), so we can always link back to it.

function flowsLink(managedSourceId: string) {
  return { label: "View in Flows", to: `/data-management/flows/${managedSourceId}` };
}

registerAction("trigger_sync", async (params) => {
  const sourceId = params.managed_source_id as string;
  const src = await triggerPipelineRun(sourceId);
  // A TRANSFORM_ONLY source on an account-level connector answers 202
  // SCHEDULED here too — same shape, same reason as trigger_transform below.
  if (isTransformScheduled(src)) {
    return { resultRef: { managed_source_id: sourceId, scheduled: true }, link: flowsLink(sourceId) };
  }
  const runId = src.recent_runs?.[0]?.id;
  return {
    resultRef: { managed_source_id: src.id, ...(runId ? { run_id: runId } : {}) },
    link: flowsLink(src.id),
  };
});

registerAction("trigger_transform", async (params) => {
  const sourceId = params.managed_source_id as string;
  const result = await triggerTransformRun(sourceId);
  // Account-level connectors answer 202 SCHEDULED — no source payload, no run
  // id yet; the run starts on the next sensor tick (plan 2026-08-04).
  if (isTransformScheduled(result)) {
    return { resultRef: { managed_source_id: sourceId, scheduled: true }, link: flowsLink(sourceId) };
  }
  const runId = result.recent_runs?.[0]?.id;
  return {
    resultRef: { managed_source_id: result.id, ...(runId ? { run_id: runId } : {}) },
    link: flowsLink(result.id),
  };
});

registerAction("trigger_backfill", async (params) => {
  const sourceId = params.managed_source_id as string;
  const src = await triggerBackfillRun(sourceId, {
    date_since: params.date_since as string,
    date_until: params.date_until as string,
    categories: params.categories as string[] | undefined,
  });
  // Account-level connectors answer 202 SCHEDULED — the restate rides the
  // next account-level run, no per-source run id exists yet (plan 2026-08-04).
  if (isTransformScheduled(src)) {
    return { resultRef: { managed_source_id: sourceId, scheduled: true }, link: flowsLink(sourceId) };
  }
  const runId = src.recent_runs?.[0]?.id;
  return {
    resultRef: { managed_source_id: src.id, ...(runId ? { run_id: runId } : {}) },
    link: flowsLink(src.id),
  };
});

// rebuild_segment — POST /segments/{id}/rebuild; returns the unchanged segment.
registerAction("rebuild_segment", async (params) => {
  const res = await rebuildSegment(params.segment_id as string);
  return {
    resultRef: { segment_id: res.segment.id },
    link: { label: "View Segment", to: `/activation/segments/${res.segment.id}` },
  };
});

// --- Assistant → paused-source recovery (plan 2026-07-29, step 4) ---
// resume_and_retry does exactly what the name says, in order: RESUME the source
// (PATCH status=ACTIVE) then RETRY the pipeline (triggerPipelineRun). If the
// resume succeeds but the trigger throws, we deliberately let that error
// propagate so the card surfaces the real reason the retry failed — we do NOT
// swallow it (the source is already un-paused by then, which is correct).
registerAction("resume_and_retry", async (params) => {
  const managedSourceId = params.managed_source_id as string;
  await updateManagedSource(managedSourceId, { status: "ACTIVE" });
  await triggerPipelineRun(managedSourceId);
  return {
    resultRef: { managed_source_id: managedSourceId },
    link: flowsLink(managedSourceId),
  };
});

// file_support_ticket flags a broken source to the Verity ops channel via the
// member-gated support-alert route. No link — the card just shows "Done".
registerAction("file_support_ticket", async (params) => {
  await fileSupportAlert(
    params.managed_source_id as string,
    params.note as string | undefined,
  );
  return { resultRef: { reported: true } };
});

// file_support_request reports a platform LIMITATION (a missing field, a
// segment that can't be expressed) rather than a broken source — hence the
// account-level route with no source id. No link — the card shows "Done".
registerAction("file_support_request", async (params) => {
  await fileSupportRequest({
    topic: params.topic as SupportTopic,
    question: params.question as string,
    blocker: params.blocker as string,
    data_source_public_id: params.data_source_public_id as string | undefined,
    table: params.table as string | undefined,
  });
  return { resultRef: { reported: true } };
});

// --- Assistant → data-chat handoff (plan 2026-07-28, step 4.1) ---
// start_data_analysis opens the question in a NEW insights conversation through
// the normal authenticated, rate-limited /chats/stream path (NOT a forgeable
// url-param auto-submit). We POST the question, read the stream only until the
// `init` event yields the new conversation id, then abort — the backend keeps
// running the analysis on a detached background task, so it completes and
// persists even though we disconnected. The user opens the linked conversation
// to see the answer.
registerAction("start_data_analysis", async (params) => {
  const { conversationId } = await streamUntilInit({
    message: params.question as string,
    workspace_id: params.workspace_id as string,
    routing_mode: "auto",
  });
  return {
    resultRef: { conversation_id: conversationId },
    link: { label: "Open in data chat", to: `/insights/chat/${conversationId}` },
  };
});

// --- Analyze drawer → full chat (plan 2026-10-09-continue-tile-analysis-in-chat §E) ---
// Opens the drawer's own "Continue in chat" choice (this conversation, or a
// private copy) through `continueInChatBridge`. The choice navigates; closing
// it without picking is a cancel, which the card turns into a release.
registerAction("continue_in_chat", async (params) => {
  const choice = await openContinueInChat(params.conversation_id as string);
  if (!choice) throw ACTION_CANCELLED;
  return { resultRef: { choice } };
});

// --- Assistant → connect-source handoff (plan 2026-07-29, step 4) ---
// Opens the shell-mounted ManagedSourceWizard pre-selected for the proposed
// connector (managed only — the wizard is never opened with transformOnly here)
// and awaits completion. On success the card flips to "Done — {label} connected"
// with a link to Data Management → Sources. On cancel/close-without-create the
// executor throws ACTION_CANCELLED, which the card turns into a release (back to
// actionable) rather than a failure — see ActionProposalCard.
registerAction("connect_source", async (params) => {
  const result = await openSourceWizard(params.connector_type as string);
  if (!result) throw ACTION_CANCELLED;
  return {
    resultRef: { data_source_id: result.dataSourceId, name: result.name },
    link: { label: "View source", to: "/data-management/sources" },
  };
});

// --- Assistant → reconnect handoff (plan 2026-07-29 assistant-reconnect-flow, step 5) ---
// Opens the shell-mounted AuthorizationDrawer pre-loaded for the paused source's
// authorization (public id, `auth_...`), where the user completes the reconnect —
// OAuth re-consent or a new API key (controls already in the drawer). On a
// successful reconnect the drawer fires onChanged, the bridge resolves
// { reconnected: true }, and the paused source auto-resumes server-side (the
// reconnect route's sweep). On close-without-reconnecting the bridge resolves
// null and the executor throws ACTION_CANCELLED, which the card turns into a
// release (back to actionable / retryable) rather than a failure — same pattern
// as connect_source.
registerAction("reconnect_source", async (params) => {
  const authorizationId = params.authorization_id as string;
  const result = await openAuthorizationDrawer(authorizationId);
  if (!result) throw ACTION_CANCELLED;
  return {
    resultRef: { authorization_id: authorizationId },
    link: { label: "View connections", to: "/data-management/authorizations" },
  };
});

// --- Assistant → reports (plan 2026-08-23 assistant-report-authoring, step 6;
// reshaped by ADR-0495) ---
// `create_report` creates a draft report under the user's JWT, the same
// POST /reports the New report modal makes, and opens it in the editor. The
// card's params are that body (the proposal tool validates them against
// `ReportCreate`), so nothing is re-derived here. `iterate_report` (an Analyst
// edition only) parks the instruction on the in-memory bridge and navigates to
// the report page, whose Edit panel runs it through its own session — the
// instruction never travels in a URL.
registerAction("create_report", async (params) => {
  const templateKey = params.template_key as string | null | undefined;
  const report = await createReport({
    workspace_public_id: params.workspace_public_id as string,
    title: params.title as string,
    composed: true,
    ...(templateKey ? { template_key: templateKey } : {}),
    period: params.period as string,
    page_format: params.page_format as "portrait" | "landscape",
    origin: "assistant",
  });
  // A report is a dashboard of kind `report` (ADR-0496 D1) and lives under
  // Reports (plan 2026-10-26, D1).
  const to = dashboardPath({ id: report.id, kind: "report" });
  return {
    resultRef: { dashboard_id: report.id },
    link: { label: "Open report", to },
    navigateTo: to,
  };
});

// `build_edition` starts a preview edition under the user's JWT, the same
// POST /dashboards/{id}/editions as the report's Build edition button (plan
// 2026-10-24-a-report-reaches-the-mcp-and-the-assistant-as-a-dashboard, G7b),
// and opens the edition, which shows its progress. One already being built
// opens that one instead; every other refusal is the button's own words.
registerAction("build_edition", async (params) => {
  const dashboardId = params.dashboard_id as string;
  let editionId: string;
  try {
    editionId = (await buildEdition(dashboardId)).public_id;
  } catch (err) {
    const refusal = editionBuildRefusal(err);
    if (refusal?.code !== "edition_in_progress" || refusal.edition == null) {
      throw refusal ? new ActionError(refusalMessage(refusal)) : err;
    }
    editionId = refusal.edition;
  }
  const to = editionPath(dashboardId, editionId);
  return {
    resultRef: { dashboard_id: dashboardId, edition_id: editionId },
    link: { label: "Open edition", to },
    navigateTo: to,
  };
});

registerAction("iterate_report", async (params) => {
  const reportId = params.report_id as string;
  const targetSectionIds = Array.isArray(params.target_section_ids)
    ? (params.target_section_ids as string[])
    : null;
  requestIterate({
    reportId,
    instruction: params.instruction as string,
    targetSectionIds,
  });
  return {
    resultRef: { report_id: reportId },
    link: { label: "Open report", to: `/reports/${reportId}` },
    navigateTo: `/reports/${reportId}`,
  };
});

// --- Assistant → business context (plan 2026-08-09, step 7) ---
// The assistant can offer to record what a customer told it about their
// business. Every one of these runs the same admin-gated /admin/context REST
// call the Business Context page uses, under the user's own JWT.

const CONTEXT_TAB = {
  business: { label: "View business profile", to: "/data-management/glossary?tab=business" },
  documents: { label: "View documents", to: "/data-management/glossary?tab=documents" },
  events: { label: "View events", to: "/data-management/glossary?tab=events" },
} as const;

/**
 * Refuse an update whose target moved since the card was made.
 *
 * Action proposals never expire, so a card confirmed a day later can be aimed
 * at a document someone has since rewritten. `expected_updated_at` is the
 * value the proposing tool saw; if the stored one differs, the edit the card
 * never saw would be silently overwritten. This is a stale-card guard, not a
 * lock — the re-read and the write are two calls, so a change landing between
 * them still wins. That race is seconds wide; the one this closes is days.
 */
function assertUnchanged(expected: unknown, actual: string | null): void {
  if (typeof expected !== "string" || !actual) return;
  if (expected !== actual) {
    throw new ActionError(
      "This has been edited since the assistant proposed the change. " +
        "Open Business Context to see the current version, or ask for a fresh proposal.",
    );
  }
}

/** A 404 on a delete means it is already gone — the desired end state. */
function isGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

// update_business_profile — PATCH-like PUT carrying ONLY the proposed fields.
// `params` is passed through as-is: the backend tool emits exactly the changed
// keys, and the route leaves omitted keys untouched. Padding the other fields
// with null here is precisely the wipe that design exists to prevent.
registerAction("update_business_profile", async (params) => {
  await updateBusinessProfile(params as BusinessProfilePatch);
  return {
    resultRef: { fields: Object.keys(params) },
    link: CONTEXT_TAB.business,
  };
});

registerAction("create_context_document", async (params) => {
  const doc = await createContextDocument({
    title: params.title as string,
    content: params.content as string,
    description: (params.description as string | undefined) ?? null,
  });
  return { resultRef: { public_id: doc.public_id }, link: CONTEXT_TAB.documents };
});

registerAction("update_context_document", async (params) => {
  const publicId = params.public_id as string;
  const current = await getContextDocument(publicId);
  assertUnchanged(params.expected_updated_at, current.updated_at);

  const doc = await updateContextDocument(publicId, {
    title: params.title as string,
    content: params.content as string,
    ...(params.description ? { description: params.description as string } : {}),
  });
  return { resultRef: { public_id: doc.public_id }, link: CONTEXT_TAB.documents };
});

registerAction("create_context_event", async (params) => {
  const event = await createContextEvent({
    title: params.title as string,
    event_date: params.event_date as string,
    end_date: (params.end_date as string | undefined) ?? null,
    category: params.category as ContextEventCategory,
    description: (params.description as string | undefined) ?? null,
  });
  return { resultRef: { public_id: event.public_id }, link: CONTEXT_TAB.events };
});

registerAction("update_context_event", async (params) => {
  const publicId = params.public_id as string;
  // No single-event GET exists; the list is small (capped per account) and is
  // what the events tab already loads.
  const current = (await listContextEvents()).find((e) => e.public_id === publicId);
  if (!current) throw new ActionError("That event no longer exists.");
  assertUnchanged(params.expected_updated_at, current.updated_at);

  const event = await updateContextEvent(publicId, {
    title: params.title as string,
    event_date: params.event_date as string,
    category: params.category as ContextEventCategory,
    ...(params.end_date
      ? { end_date: params.end_date as string }
      : { clear_end_date: true }),
    ...(params.description ? { description: params.description as string } : {}),
  });
  return { resultRef: { public_id: event.public_id }, link: CONTEXT_TAB.events };
});

registerAction("delete_context_document", async (params) => {
  const publicId = params.public_id as string;
  try {
    const current = await getContextDocument(publicId);
    assertUnchanged(params.expected_updated_at, current.updated_at);
  } catch (err) {
    // Already deleted — that IS the requested end state, so report success
    // rather than an error the user can do nothing about.
    if (isGone(err)) return { resultRef: { public_id: publicId, already_deleted: true } };
    throw err;
  }
  try {
    await deleteContextDocument(publicId);
  } catch (err) {
    if (!isGone(err)) throw err;
  }
  return { resultRef: { public_id: publicId }, link: CONTEXT_TAB.documents };
});

registerAction("delete_context_event", async (params) => {
  const publicId = params.public_id as string;
  const current = (await listContextEvents()).find((e) => e.public_id === publicId);
  if (!current) return { resultRef: { public_id: publicId, already_deleted: true } };
  assertUnchanged(params.expected_updated_at, current.updated_at);
  try {
    await deleteContextEvent(publicId);
  } catch (err) {
    if (!isGone(err)) throw err;
  }
  return { resultRef: { public_id: publicId }, link: CONTEXT_TAB.events };
});
